// src/hooks/useFeedbackContext.ts
// Assembles the diagnostic context attached to a feedback submission.
//
// Why this exists: before it, feedback was text only, so "the audit looks wrong"
// cost a round-trip every time — which page, which plan, which build, what screen.
//
// Two rules govern everything here:
//
//   1. BEST EFFORT, including against HANGS. Every field is optional, every read
//      is guarded, and the one network call is time-bounded. A failure — or a
//      stall — while determining the app version must never stop someone
//      reporting a bug; a diagnostics feature that breaks the thing it
//      diagnoses is worse than none.
//   2. PATH ONLY, never the query string or fragment. A query string can carry
//      ids, tokens and search terms the reporter never meant to attach.
//
// The server does not trust any of this: routes/feedback.py re-applies an
// allowlist and a size cap. This is convenience, not the security boundary.
import { useCallback } from 'react';
import { useActiveInstitution } from '../contexts/ActiveInstitutionContext';

/**
 * Keys the backend allowlist accepts. Anything else is dropped server-side.
 *
 * A `type` alias, not an `interface`, deliberately: the generated client types
 * `context` as `{ [key: string]: unknown }`, and TypeScript refuses to assign an
 * interface to an index-signature type (interfaces are open to declaration
 * merging, so their keys are not statically known). A type alias gets an
 * implicit index signature and assigns cleanly — without casting at the call
 * site, which would throw away the checking this exists to provide.
 */
export type FeedbackContext = {
  route?: string;
  plan_id?: number;
  institution?: string;
  app_version?: string;
  viewport?: string;
  user_agent?: string;
};

/** Bound on the one network call. Generous for a health check, short enough
 *  that nobody waits on it to file a report. */
const VERSION_FETCH_TIMEOUT_MS = 1500;

/** Cached for the tab's lifetime — the backend version cannot change under us. */
let cachedVersion: string | null = null;
let versionFetch: Promise<string | null> | null = null;

/**
 * The deployed backend version.
 *
 * Read from /api/health rather than frontend package.json, which is stale
 * (pinned at 2.1.0 and unmaintained). The endpoint is unauthenticated,
 * CORS-allowed and exempt from host enforcement, so a plain fetch is fine —
 * it is deliberately NOT in the typed client.
 *
 * Caveat worth knowing: the merge-time version bump has been missed before
 * (issue #266), so two different builds can report the same version. It
 * narrows the field; it does not identify a build uniquely.
 */
async function fetchAppVersion(): Promise<string | null> {
  if (cachedVersion !== null) return cachedVersion;
  if (versionFetch) return versionFetch;

  versionFetch = (async () => {
    try {
      const base = process.env.REACT_APP_API_URL || '';
      // Timeout, not just try/catch. Every failure path here is caught, but a
      // HANG throws nothing: buildContext() awaits this and handleSubmit awaits
      // buildContext(), so a stalled /api/health would leave the button on
      // "Sending..." indefinitely with nothing sent — most likely exactly when
      // someone is reporting that the app is broken. The abort turns the hang
      // into the error the catch below already handles.
      //
      // AbortController + setTimeout rather than AbortSignal.timeout(), which
      // is unsupported below Safari 16 / Chrome 103 AND absent from jsdom. On
      // those, `AbortSignal.timeout(...)` throws a TypeError inside this try —
      // so the guard would silently not exist on exactly the older browsers,
      // and no test could see it. A written-out test caught this.
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), VERSION_FETCH_TIMEOUT_MS);
      let res;
      try {
        res = await fetch(`${base}/api/health`, {
          credentials: 'omit',
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timer);
      }
      if (!res.ok) return null;
      const body = await res.json();
      const version = typeof body?.version === 'string' ? body.version : null;
      cachedVersion = version;
      return version;
    } catch {
      return null; // best effort — never surfaces to the user
    } finally {
      versionFetch = null;
    }
  })();

  return versionFetch;
}

/** Test seam: the module-level cache would otherwise leak between tests. */
export function __resetFeedbackVersionCache(): void {
  cachedVersion = null;
  versionFetch = null;
}

export interface UseFeedbackContextOptions {
  /**
   * The plan the reporter is looking at. Threaded from AuthenticatedApp, which
   * owns it — it is NOT on AppContext, and calling useAcademicPlans() here would
   * fire an extra /api/academic-plans request per modal open, since that hook is
   * plain useState/useEffect rather than context-backed.
   */
  planId?: number | null;
}

/**
 * Returns a builder rather than a value: the context is assembled at SUBMIT
 * time, so it records where the reporter actually was when they described the
 * problem, not where they were when the modal mounted.
 */
export function useFeedbackContext({ planId }: UseFeedbackContextOptions = {}) {
  const { activeSlug } = useActiveInstitution();

  return useCallback(async (): Promise<FeedbackContext> => {
    const context: FeedbackContext = {};

    try {
      // Read from window rather than useLocation(). The route is wanted ONCE, at
      // submit time — there is nothing to re-render on — and depending on a
      // router hook would make this modal unmountable outside a <Router>, which
      // broke 31 existing tests that render it bare. The app uses BrowserRouter,
      // so window.location.pathname IS the route.
      //
      // Path only: .search and .hash are deliberately not read. A query string
      // can carry ids and search terms the reporter never meant to attach.
      if (typeof window !== 'undefined' && window.location?.pathname) {
        context.route = window.location.pathname;
      }
    } catch {
      /* best effort */
    }

    try {
      if (typeof planId === 'number' && Number.isFinite(planId)) {
        context.plan_id = planId;
      }
    } catch {
      /* best effort */
    }

    try {
      if (activeSlug) context.institution = activeSlug;
    } catch {
      /* best effort */
    }

    try {
      if (typeof window !== 'undefined' && window.innerWidth && window.innerHeight) {
        context.viewport = `${window.innerWidth}x${window.innerHeight}`;
      }
    } catch {
      /* best effort */
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.userAgent) {
        context.user_agent = navigator.userAgent;
      }
    } catch {
      /* best effort */
    }

    try {
      const version = await fetchAppVersion();
      if (version) context.app_version = version;
    } catch {
      /* best effort */
    }

    return context;
  }, [planId, activeSlug]);
}

export default useFeedbackContext;
